import { useCallback } from 'react';

import useRouter from './useRouter';
import { useTabs, OpenTabParams } from 'contexts/TabContext';
import { buildRecordPath } from 'constants/tabs';

/**
 * Opens a record the way the current settings say it should be opened.
 *
 * One place decides between the three outcomes, so a link, a menu item, and a button all behave the
 * same and none of them has to know about tabs:
 *
 *   - tab bar on and the user may see it  → the record opens as an in-app tab
 *   - tab bar off                          → a new browser tab, exactly as before this feature
 *   - user lacks the module permission     → normal navigation, so ConditionalRoute sends them to /notfound
 */
const useOpenRecord = () => {
  const { history } = useRouter();
  const { isTabNavActive, openTab } = useTabs();

  return useCallback(
    (params: OpenTabParams) => {
      if (openTab(params)) {
        return;
      }

      // No tab to hold it, so the section the link asked for has to survive in the URL itself — a
      // new browser window cannot be handed location.state at all.
      const recordPath = buildRecordPath(params.recordType, params.recordId);
      const path = params.innerTab === undefined ? recordPath : `${recordPath}?tab=${params.innerTab}`;

      if (isTabNavActive) {
        history.push(path);
        return;
      }

      window.open(path, '_blank');
    },
    [history, isTabNavActive, openTab]
  );
};

export default useOpenRecord;
